import { useCallback, useState } from "react";
import Link from "next/link";
import { useQueryClient } from "@tanstack/react-query";

import { AgentDetails, CommonAgentSummary } from "@bagel-web/common";
import { CopyValue, Inspector } from "@bagel-web/components";
import { getAgentLabel } from "../util";

const AgentName = ({
  sessionId,
  agentId,
  agentSummary,
  fetchAgentSummary,
  showLink = false,
  showInspector = true,
}: {
  sessionId: string;
  agentId: string;
  showLink?: boolean;
  showInspector?: boolean;
} & (
  | {
      agentSummary?: never;
      fetchAgentSummary: (
        sessionId: string,
        agentId: string
      ) => Promise<CommonAgentSummary | null>;
    }
  | { agentSummary: CommonAgentSummary; fetchAgentSummary?: never }
)) => {
  const [agentDetails, setAgentDetails] = useState<CommonAgentSummary | null>(
    agentSummary ?? null
  );
  const queryClient = useQueryClient();

  const handlePreviewAgent = useCallback(async () => {
    if (agentDetails || !fetchAgentSummary) {
      return;
    }
    const agentData = await fetchAgentSummary(sessionId, agentId);
    setAgentDetails(agentData);
  }, [queryClient, setAgentDetails, agentDetails, fetchAgentSummary]);

  const agentLabel = agentSummary ? getAgentLabel(agentSummary) : agentId;

  const agentName = showLink ? (
    <Link href={`/sessions/${sessionId}/agent/${agentId}`}>{agentLabel}</Link>
  ) : (
    agentLabel
  );

  const copyValue = <CopyValue value={agentId}>{agentName}</CopyValue>;

  return showInspector ? (
    <Inspector
      View={AgentDetails}
      viewProps={{
        agent: agentDetails,
      }}
      onEnter={handlePreviewAgent}
      showLoading={!agentDetails}
    >
      {copyValue}
    </Inspector>
  ) : (
    copyValue
  );
};

export default AgentName;
